export interface ThemeColors {
  primary: {
    background: string;
    text: string;
  };
  secondary: {
    background: string;
    text: string;
  };
  accent: {
    background: string;
    text: string;
  };
  surface: {
    background: string;
    text: string;
  };
  border: string;
  shadow: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
}

export const themes: Theme[] = [
  {
    id: 'tmos-light',
    name: 'T-MOS Light',
    description: 'Light theme with white sidebar/navbar and light grey content area - green and white text',
    colors: {
      primary: {
        background: '#ffffff', // White - for sidebar and top bar
        text: '#1a5d2e' // Green text for white background
      },
      secondary: {
        background: '#f3f4f6', // Gray-100 - for main content area (light grey)
        text: '#1a5d2e' // Green text for main content
      },
      accent: {
        background: '#1a5d2e', // Green - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f3f4f6', // Same as secondary - cards blend into background (unified scheme)
        text: '#1a5d2e' // Green text
      },
      border: '#d1d5db', // Gray-300 - two shades darker than gray-100 background
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#1a5d2e' // Green for info (matches accent)
    }
  },
  {
    id: 'tmos-advantage',
    name: 'T-MOS Dark',
    description: 'Dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e293b', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (slate-50)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#3b82f6', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#cbd5e1', // Slate-300 - two shades darker than slate-50 background
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'tmos-blue',
    name: 'TMOS Blue',
    description: 'Professional blue theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e3a8a', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (slate-50)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#3b82f6', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#cbd5e1', // Slate-300 - two shades darker than slate-50 background
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'midnight-slate',
    name: 'Midnight Slate',
    description: 'Dark professional theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#0f172a', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#3b82f6', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'emerald-forest',
    name: 'Emerald Forest',
    description: 'Rich green theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#064e3b', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#10b981', // Green - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'royal-amethyst',
    name: 'Royal Amethyst',
    description: 'Elegant purple theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#581c87', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#7c3aed', // Purple - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'ocean-depths',
    name: 'Ocean Depths',
    description: 'Deep blue theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#0c4a6e', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#0ea5e9', // Sky blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6'
    }
  },
  {
    id: 'professional-dark',
    name: 'Professional Dark',
    description: 'Professional dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e1e1e', // Very dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#2d2d30', // Dark gray - for main content area (2 shades lighter)
        text: '#cccccc' // Light text for dark background
      },
      accent: {
        background: '#007acc', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#2d2d30', // Same as secondary - cards blend into background
        text: '#cccccc' // Light text
      },
      border: '#3c3c3c', // Medium gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#4ec9b0',
      warning: '#dcdcaa',
      error: '#f44747',
      info: '#569cd6'
    }
  },
  {
    id: 'professional-light',
    name: 'Professional Light',
    description: 'Clean light theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e293b', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#007acc', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#28a745',
      warning: '#ffc107',
      error: '#dc3545',
      info: '#007acc'
    }
  },
  {
    id: 'code-dark',
    name: 'Code Dark',
    description: 'Developer-friendly dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#0d1117', // GitHub dark - for sidebar and top bar
        text: '#f0f6fc' // Light text for dark background
      },
      secondary: {
        background: '#161b22', // Dark gray - for main content area (2 shades lighter)
        text: '#f0f6fc' // Light text for dark background
      },
      accent: {
        background: '#238636', // Green - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#161b22', // Same as secondary - cards blend into background
        text: '#f0f6fc' // Light text
      },
      border: '#30363d', // Dark gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.3)', // Subtle shadow
      success: '#3fb950',
      warning: '#d29922',
      error: '#f85149',
      info: '#58a6ff'
    }
  },
  {
    id: 'code-light',
    name: 'Code Light',
    description: 'Developer-friendly light theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#24292f', // GitHub dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f6f8fa', // Light gray - for main content area (2 shades lighter)
        text: '#24292f' // Dark text for light background
      },
      accent: {
        background: '#0969da', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f6f8fa', // Same as secondary - cards blend into background
        text: '#24292f' // Dark text
      },
      border: '#d0d7de', // Light gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#1a7f37',
      warning: '#9a6700',
      error: '#cf222e',
      info: '#0969da'
    }
  },
  {
    id: 'studio-dark',
    name: 'Studio Dark',
    description: 'IDE-style dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e1e1e', // VS Code dark - for sidebar and top bar
        text: '#cccccc' // Light text for dark background
      },
      secondary: {
        background: '#252526', // Dark gray - for main content area (2 shades lighter)
        text: '#cccccc' // Light text for dark background
      },
      accent: {
        background: '#007acc', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#252526', // Same as secondary - cards blend into background
        text: '#cccccc' // Light text
      },
      border: '#3c3c3c', // Medium gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#4ec9b0',
      warning: '#dcdcaa',
      error: '#f44747',
      info: '#569cd6'
    }
  },
  {
    id: 'studio-light',
    name: 'Studio Light',
    description: 'IDE-style light theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e293b', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#f8fafc', // Light grey - for main content area (2 shades lighter)
        text: '#1e293b' // Dark text for light background
      },
      accent: {
        background: '#0078d4', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#f8fafc', // Same as secondary - cards blend into background
        text: '#1e293b' // Dark text
      },
      border: '#e2e8f0', // Very light border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#107c10',
      warning: '#ff8c00',
      error: '#d13438',
      info: '#0078d4'
    }
  },
  {
    id: 'vibrant-dark',
    name: 'Vibrant Dark',
    description: 'High-contrast dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#272822', // Monokai dark - for sidebar and top bar
        text: '#f8f8f2' // Light text for dark background
      },
      secondary: {
        background: '#3e3d32', // Dark olive - for main content area (2 shades lighter)
        text: '#f8f8f2' // Light text for dark background
      },
      accent: {
        background: '#66d9ef', // Cyan - for buttons and interactive elements
        text: '#272822' // Dark text for buttons
      },
      surface: {
        background: '#3e3d32', // Same as secondary - cards blend into background
        text: '#f8f8f2' // Light text
      },
      border: '#49483e', // Medium olive border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#a6e22e',
      warning: '#e6db74',
      error: '#f92672',
      info: '#66d9ef'
    }
  },
  {
    id: 'elegant-dark',
    name: 'Elegant Dark',
    description: 'Sophisticated dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#282a36', // Dracula dark - for sidebar and top bar
        text: '#f8f8f2' // Light text for dark background
      },
      secondary: {
        background: '#44475a', // Dracula gray - for main content area (2 shades lighter)
        text: '#f8f8f2' // Light text for dark background
      },
      accent: {
        background: '#bd93f9', // Purple - for buttons and interactive elements
        text: '#282a36' // Dark text for buttons
      },
      surface: {
        background: '#44475a', // Same as secondary - cards blend into background
        text: '#f8f8f2' // Light text
      },
      border: '#6272a4', // Dracula blue border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#50fa7b',
      warning: '#ffb86c',
      error: '#ff5555',
      info: '#8be9fd'
    }
  },
  {
    id: 'readable-dark',
    name: 'Readable Dark',
    description: 'Scientifically designed dark theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#002b36', // Solarized dark - for sidebar and top bar
        text: '#93a1a1' // Light text for dark background
      },
      secondary: {
        background: '#073642', // Dark teal - for main content area (2 shades lighter)
        text: '#93a1a1' // Light text for dark background
      },
      accent: {
        background: '#268bd2', // Blue - for buttons and interactive elements
        text: '#fdf6e3' // Light text for buttons
      },
      surface: {
        background: '#073642', // Same as secondary - cards blend into background
        text: '#93a1a1' // Light text
      },
      border: '#586e75', // Medium gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#859900',
      warning: '#b58900',
      error: '#dc322f',
      info: '#268bd2'
    }
  },
  {
    id: 'readable-light',
    name: 'Readable Light',
    description: 'Scientifically designed light theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#1e293b', // Dark - for sidebar and top bar
        text: '#ffffff' // Light text for dark background
      },
      secondary: {
        background: '#fdf6e3', // Light cream - for main content area (2 shades lighter)
        text: '#586e75' // Dark text for light background
      },
      accent: {
        background: '#268bd2', // Blue - for buttons and interactive elements
        text: '#ffffff' // White text for buttons
      },
      surface: {
        background: '#fdf6e3', // Same as secondary - cards blend into background
        text: '#586e75' // Dark text
      },
      border: '#93a1a1', // Medium gray border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.05)', // Subtle shadow
      success: '#859900',
      warning: '#b58900',
      error: '#dc322f',
      info: '#268bd2'
    }
  },
  {
    id: 'arctic',
    name: 'Arctic',
    description: 'Cool-toned professional theme with unified appearance - cards blend into background',
    colors: {
      primary: {
        background: '#2e3440', // Nord dark - for sidebar and top bar
        text: '#d8dee9' // Light text for dark background
      },
      secondary: {
        background: '#3b4252', // Nord gray - for main content area (2 shades lighter)
        text: '#d8dee9' // Light text for dark background
      },
      accent: {
        background: '#88c0d0', // Nord blue - for buttons and interactive elements
        text: '#2e3440' // Dark text for buttons
      },
      surface: {
        background: '#3b4252', // Same as secondary - cards blend into background
        text: '#d8dee9' // Light text
      },
      border: '#4c566a', // Nord border - subtle distinction
      shadow: 'rgba(0, 0, 0, 0.2)', // Subtle shadow
      success: '#a3be8c',
      warning: '#ebcb8b',
      error: '#bf616a',
      info: '#88c0d0'
    }
  }
];

export const getThemeById = (id: string): Theme => {
  return themes.find(theme => theme.id === id) || themes[0];
};

export const getDefaultTheme = (): Theme => {
  // Return T-MOS Light as the default theme
  return themes.find(theme => theme.id === 'tmos-light') || themes[0];
};
